/**
 * Wide-logo framing (org-header-logo-fit, D20–D25): trims the empty space around
 * an organisation's wide logo and bakes a chosen size ("zoom") into a PNG whose
 * shape matches the employee app's header logo box, so the app needs no change.
 *
 * Header measurements below are MIRRORED from employee-app (athlyn2 design
 * system) as read at employee-app `origin/main@486926f` (PR 941). If the app
 * header changes, update these constants and the preview with it.
 *
 * This file holds the pure geometry (T1) and the canvas-based `frameWideLogo`
 * (decode → trim → draw → PNG) plus `measureSavedWideLogo` (T2).
 */

import { withLogoCacheBuster } from "@/lib/logoContrast";

/**
 * The app's wide-logo box in points.
 * employee-app `src/config/designSystems.ts:10792-10793` — athlyn2
 * `header.orgLogoMaxWidth` = 180, `header.orgLogoHeight` = 40; drawn in
 * `src/components/home/HomeScreenHeader.tsx:124-125` with
 * `contentFit="contain"` `contentPosition="left"` (`:136-137`).
 */
export const HEADER_LOGO_BOX = { width: 180, height: 40 } as const;

/** Pixel size of the baked wide-logo PNG: `HEADER_LOGO_BOX` × 8 (same 4.5:1 ratio). */
export const HEADER_FRAME_PX = { width: 1440, height: 320 } as const;

/** Header-size slider range (D23): 60 %–100 %, step 5 %; 100 % = trimmed logo fills the box. */
export const ZOOM_MIN = 0.6;
export const ZOOM_MAX = 1;
export const ZOOM_STEP = 0.05;

/** Preview phone width in points (D25 — a standard 390-pt-wide phone; 1 CSS px = 1 pt). */
export const PREVIEW_PHONE_WIDTH = 390;
/** Preview status-bar area in points (D25 — fixed stand-in for `insets.top`). */
export const PREVIEW_STATUS_BAR = 47;

/**
 * athlyn2 home-header measurements the preview mirrors (points).
 * - `topPadding` 8: `designSystems.ts:10791` `header.topPadding`; applied as
 *   `insets.top + headerTokens.topPadding` in `HomeScreenHeader.tsx:98-100`.
 * - `horizontalPadding` 20: `HomeScreenHeader.tsx:98-100` `paddingHorizontal: 20`.
 * - `bottomPadding` 12: `designSystems.ts:10773` `header.bottomPadding`
 *   (`HomeScreenHeader.tsx:98-100`).
 * - `iconSize` 40, `iconGap` 12, `avatarBorder` 2: `HomeScreenHeader.tsx` bell and
 *   avatar are 40 × 40 with `marginRight: 12`; the avatar has a 2-pt border.
 */
export const HEADER_MIRROR = {
  topPadding: 8,
  horizontalPadding: 20,
  bottomPadding: 12,
  iconSize: 40,
  iconGap: 12,
  avatarBorder: 2,
} as const;

/** A pixel counts as logo content when its alpha is at least this (0–255). */
export const CONTENT_ALPHA_THRESHOLD = 8;

export interface ContentBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Frame {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Clamps a zoom to `ZOOM_MIN`–`ZOOM_MAX`; a non-finite value means 100 %. */
export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return ZOOM_MAX;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));
}

/**
 * Bounding box of the pixels whose alpha is ≥ `alphaThreshold` in an RGBA
 * buffer of `width × height`. `null` when no pixel qualifies (fully transparent).
 */
export function findContentBox(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  alphaThreshold: number = CONTENT_ALPHA_THRESHOLD,
): ContentBox | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    const row = y * width * 4;
    for (let x = 0; x < width; x++) {
      if (rgba[row + x * 4 + 3] < alphaThreshold) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

/**
 * Where trimmed content of `contentW × contentH` is drawn inside a `boxW × boxH`
 * box: fit-contain × zoom (zoom clamped 0.6–1.0), left-aligned (x = 0),
 * vertically centred — the same placement as the app's `contain` + `left`.
 */
export function computeFrame({
  contentW,
  contentH,
  boxW,
  boxH,
  zoom,
}: {
  contentW: number;
  contentH: number;
  boxW: number;
  boxH: number;
  zoom: number;
}): Frame {
  if (contentW <= 0 || contentH <= 0) return { x: 0, y: boxH / 2, width: 0, height: 0 };
  const scale = Math.min(boxW / contentW, boxH / contentH) * clampZoom(zoom);
  const width = contentW * scale;
  const height = contentH * scale;
  return { x: 0, y: (boxH - height) / 2, width, height };
}

/**
 * The zoom an existing saved image represents in a `boxW × boxH` box: the
 * content's drawn height when the WHOLE image is fitted (contain) into the box,
 * divided by its height in `computeFrame` at zoom 1. Unclamped and unrounded —
 * see `startingZoom` for the slider value. Degenerate input ⇒ 1.
 */
export function measureZoom({
  imageW,
  imageH,
  contentBox,
  boxW,
  boxH,
}: {
  imageW: number;
  imageH: number;
  contentBox: ContentBox;
  boxW: number;
  boxH: number;
}): number {
  if (imageW <= 0 || imageH <= 0 || contentBox.width <= 0 || contentBox.height <= 0) return ZOOM_MAX;
  const wholeScale = Math.min(boxW / imageW, boxH / imageH);
  const full = computeFrame({ contentW: contentBox.width, contentH: contentBox.height, boxW, boxH, zoom: 1 });
  return (contentBox.height * wholeScale) / full.height;
}

/** Slider starting value: a measured zoom rounded to the nearest `ZOOM_STEP`, clamped 0.6–1.0. */
export function startingZoom(measured: number): number {
  if (!Number.isFinite(measured)) return ZOOM_MAX;
  const rounded = Math.round(measured / ZOOM_STEP) * ZOOM_STEP;
  return clampZoom(Number(rounded.toFixed(2)));
}

/** An RGBA buffer with its size (the shape of `ImageData` and of `loadLogoPixels`'s result). */
export interface RgbaPixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/** Copies the pixels inside `box` (assumed within the image) into a new buffer. */
export function cropPixels(pixels: RgbaPixels, box: ContentBox): RgbaPixels {
  const data = new Uint8ClampedArray(box.width * box.height * 4);
  for (let y = 0; y < box.height; y++) {
    const from = ((box.y + y) * pixels.width + box.x) * 4;
    data.set(pixels.data.subarray(from, from + box.width * 4), y * box.width * 4);
  }
  return { data, width: box.width, height: box.height };
}

/**
 * The image cropped to its content box, so empty padding does not count in the
 * logo visibility check (D17's "own background" share). A fully transparent
 * image is returned unchanged (the check then reports nothing visible).
 */
export function cropToContent(pixels: RgbaPixels): RgbaPixels {
  const box = findContentBox(pixels.data, pixels.width, pixels.height);
  return box ? cropPixels(pixels, box) : pixels;
}

/**
 * Area-average downsample to at most `maxWidth` wide (height keeps the aspect,
 * rounded, ≥ 1 — the same size `loadLogoPixels` reads at). Averaged with
 * premultiplied alpha, as a canvas scales, so transparent neighbours fade a
 * pixel's alpha without darkening its colour. Narrow enough ⇒ the input itself.
 */
export function downsamplePixels(pixels: RgbaPixels, maxWidth: number): RgbaPixels {
  const { data: src, width: W, height: H } = pixels;
  if (W <= maxWidth) return pixels;
  const w = maxWidth;
  const h = Math.max(1, Math.round((H * w) / W));
  const data = new Uint8ClampedArray(w * h * 4);
  for (let dy = 0; dy < h; dy++) {
    const y0 = Math.floor((dy * H) / h);
    const y1 = Math.max(y0 + 1, Math.floor(((dy + 1) * H) / h));
    for (let dx = 0; dx < w; dx++) {
      const x0 = Math.floor((dx * W) / w);
      const x1 = Math.max(x0 + 1, Math.floor(((dx + 1) * W) / w));
      let a = 0;
      let r = 0;
      let g = 0;
      let b = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const o = (y * W + x) * 4;
          const alpha = src[o + 3];
          a += alpha;
          r += src[o] * alpha;
          g += src[o + 1] * alpha;
          b += src[o + 2] * alpha;
        }
      }
      const o = (dy * w + dx) * 4;
      const n = (y1 - y0) * (x1 - x0);
      if (a > 0) {
        data[o] = r / a;
        data[o + 1] = g / a;
        data[o + 2] = b / a;
      }
      data[o + 3] = a / n;
    }
  }
  return { data, width: w, height: h };
}

// ---------------------------------------------------------------------------
// Canvas side (T2): decode → find content → crop → frame → PNG.
// ---------------------------------------------------------------------------

/** The content box is first found on a copy downsampled to at most this width. */
export const FRAMING_SCAN_MAX_WIDTH = 512;

/** File name for a framing of a saved (URL) logo — there is no original name to keep. */
export const SAVED_LOGO_FRAMED_NAME = "wide-logo-framed.png";

export interface WideLogoFraming {
  /** The framed PNG (1440 × 320, transparent, content left-aligned + vertically centred). */
  file: File;
  /** The trimmed content pixels at full resolution (no padding) — for the visibility check. */
  trimmed: ImageData;
}

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = withLogoCacheBuster(url);
  });
}

/** Full-resolution decode: `createImageBitmap` for a picked file, a CORS `Image` for a saved URL. */
async function decode(source: File | string): Promise<Decoded | null> {
  if (typeof source === "string") {
    const img = await loadImage(source);
    if (!img) return null;
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, release: () => {} };
  }
  if (typeof createImageBitmap !== "function") return null;
  try {
    const bitmap = await createImageBitmap(source);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close?.() };
  } catch {
    return null;
  }
}

/**
 * A 2D context on a fresh canvas. `readBack` ⇒ `willReadFrequently`, so a canvas
 * read with `getImageData` is kept CPU-side (no GPU readback, no Chrome warning);
 * the PNG output canvas is never read back and stays GPU-accelerated.
 */
function canvas2d(width: number, height: number, readBack = false): CanvasRenderingContext2D | null {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return readBack ? canvas.getContext("2d", { willReadFrequently: true }) : canvas.getContext("2d");
}

function toPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    try {
      // Throws a SecurityError on a tainted canvas.
      canvas.toBlob((blob) => resolve(blob), "image/png");
    } catch (error) {
      reject(error);
    }
  });
}

/** `logo.png` ⇒ `logo-framed.png`; `my.logo.svg` ⇒ `my.logo-framed.png`; a URL source ⇒ `wide-logo-framed.png`. */
function framedName(source: File | string): string {
  if (typeof source === "string") return SAVED_LOGO_FRAMED_NAME;
  const dot = source.name.lastIndexOf(".");
  const base = dot > 0 ? source.name.slice(0, dot) : source.name;
  return `${base || "wide-logo"}-framed.png`;
}

interface LocatedContent {
  /** Top-left of the full-resolution region scanned in step 2c, in image pixels. */
  rx: number;
  ry: number;
  /** The exact content box, relative to the region. */
  exact: ContentBox;
  /** The region's read-back context (full resolution). */
  region: CanvasRenderingContext2D;
}

/**
 * The exact content box of a decoded image (steps 2a–2c of `frameWideLogo`):
 * coarse scan on a copy ≤ `FRAMING_SCAN_MAX_WIDTH` wide, scaled back up with one
 * sample pixel of margin, then refined on the full-resolution region. `null` for
 * an empty decode, no 2D context or no visible content; a tainted canvas throws
 * (callers catch it).
 */
function locateContent(decoded: Decoded): LocatedContent | null {
  const { width: W, height: H } = decoded;
  if (!W || !H) return null;

  // 2a. Coarse scan on a downsampled copy.
  const sw = Math.min(FRAMING_SCAN_MAX_WIDTH, W);
  const sh = Math.max(1, Math.round((H * sw) / W));
  const scan = canvas2d(sw, sh, true);
  if (!scan) return null;
  scan.drawImage(decoded.source, 0, 0, sw, sh);
  const coarse = findContentBox(scan.getImageData(0, 0, sw, sh).data, sw, sh);
  if (!coarse) return null;

  // 2b. Scale back up with one sample pixel of margin, clamped to the image.
  const fx = W / sw;
  const fy = H / sh;
  const rx = Math.max(0, Math.floor((coarse.x - 1) * fx));
  const ry = Math.max(0, Math.floor((coarse.y - 1) * fy));
  const rw = Math.min(W, Math.ceil((coarse.x + coarse.width + 1) * fx)) - rx;
  const rh = Math.min(H, Math.ceil((coarse.y + coarse.height + 1) * fy)) - ry;

  // 2c. Exact box on the full-resolution region.
  const region = canvas2d(rw, rh, true);
  if (!region) return null;
  region.drawImage(decoded.source, rx, ry, rw, rh, 0, 0, rw, rh);
  const exact = findContentBox(region.getImageData(0, 0, rw, rh).data, rw, rh);
  if (!exact) return null;
  return { rx, ry, exact, region };
}

/**
 * Trims the empty space around a wide logo and bakes it, at `zoom`, into a
 * transparent `HEADER_FRAME_PX` PNG with the app header box's ratio:
 * 1. decode at full resolution;
 * 2. find the content box on a copy ≤ `FRAMING_SCAN_MAX_WIDTH` wide, scale it back up
 *    (one sample pixel of margin), then refine it exactly on the full-resolution region;
 * 3. draw the exact crop at `computeFrame(...)` × 8 (left-aligned, vertically centred)
 *    with high-quality smoothing.
 * `"unavailable"` on a decode failure, no 2D context, a tainted canvas
 * (`getImageData`/`toBlob`) or an image with no visible content.
 */
export async function frameWideLogo(
  source: File | string,
  zoom: number,
): Promise<WideLogoFraming | "unavailable"> {
  const decoded = await decode(source);
  if (!decoded) return "unavailable";
  try {
    const found = locateContent(decoded);
    if (!found) return "unavailable";
    const { rx, ry, exact, region } = found;
    const trimmed = region.getImageData(exact.x, exact.y, exact.width, exact.height);

    // 3. Frame.
    const scale = HEADER_FRAME_PX.width / HEADER_LOGO_BOX.width;
    const f = computeFrame({
      contentW: exact.width,
      contentH: exact.height,
      boxW: HEADER_LOGO_BOX.width,
      boxH: HEADER_LOGO_BOX.height,
      zoom,
    });
    const frame = canvas2d(HEADER_FRAME_PX.width, HEADER_FRAME_PX.height);
    if (!frame) return "unavailable";
    frame.imageSmoothingEnabled = true;
    frame.imageSmoothingQuality = "high";
    frame.drawImage(
      decoded.source,
      rx + exact.x,
      ry + exact.y,
      exact.width,
      exact.height,
      f.x * scale,
      f.y * scale,
      f.width * scale,
      f.height * scale,
    );
    const blob = await toPngBlob(frame.canvas);
    if (!blob) return "unavailable";
    return { file: new File([blob], framedName(source), { type: "image/png" }), trimmed };
  } catch {
    // SecurityError (tainted canvas) or any other canvas failure.
    return "unavailable";
  } finally {
    decoded.release();
  }
}

/**
 * The slider's starting value for a SAVED wide logo: full-resolution decode →
 * the same coarse-then-exact content box `frameWideLogo` uses → `measureZoom`
 * → `startingZoom`. (A ≤ 256-px sample blurs the edges by about a pixel, which
 * could land the start one 5 % step off.) `"unavailable"` when the browser
 * cannot read the image (e.g. no storage CORS, a tainted canvas) or it has no
 * visible content — the slider is then disabled with the re-upload note (D26).
 */
export async function measureSavedWideLogo(url: string): Promise<number | "unavailable"> {
  const decoded = await decode(url);
  if (!decoded) return "unavailable";
  try {
    const found = locateContent(decoded);
    if (!found) return "unavailable";
    const { rx, ry, exact } = found;
    return startingZoom(
      measureZoom({
        imageW: decoded.width,
        imageH: decoded.height,
        contentBox: { x: rx + exact.x, y: ry + exact.y, width: exact.width, height: exact.height },
        boxW: HEADER_LOGO_BOX.width,
        boxH: HEADER_LOGO_BOX.height,
      }),
    );
  } catch {
    // SecurityError (tainted canvas).
    return "unavailable";
  } finally {
    decoded.release();
  }
}
